import UaParser from 'ua-parser-js';

import { getEnv } from '@luna/env';
import { isFeatureEnabled } from '@luna/gatekeeper';
import { request } from '@luna/api';
import { logger } from '@luna/logger';
import { getFeatureConfig } from '@luna/feature-config';

import type { Tracker } from '../model/Tracker';
import { EventQueue } from './EventQueue';
import type { TrackingEvent } from '../model/TrackingEvent';
import type { BaseTrackEvent } from '../model/trackingTypes';
import { getPostHogCrossApplicationTrackingProperties } from './PostHogTrackerService';
import { skipTracking } from './helpers/skipTracking';
import { shouldTrackEventHistory } from './helpers/shouldTrackEventHistory';
import { queryExternalIdentifiersIfNeeded } from './ExternalIdentifiersQuery';

interface PostHogDto {
    api_key: string;
    event: string;
    $timestamp: string;
    distinct_id: string;
    properties: {
        $session_id: string;
        application: string;
        $app_version: string;
        $current_url: string;
        normalized: string;
        platform: string;
        $os?: string;
        $device_type?: string;
        $browser?: string;
        $browser_version?: string;
        category?: string;
        eventType?: string;
        app_language?: string;
        $os_version?: string;
        app_country?: string;
        user_id?: string;
        $groups?: {
            license_id?: string;
        };
    };
}

type PostHogDataConsentHandler = () => Promise<boolean>;
type IsJuniorHandler = () => Promise<boolean>;

export interface PostHogTrackerConfig {
    dataConsentHandler: PostHogDataConsentHandler;
    isJuniorHandler?: IsJuniorHandler;
}

const NO_CONSENT = false;
const DEFAULT_DATA_CONSENT_HANDLER = async (): Promise<boolean> => NO_CONSENT;

export class PostHogTracker implements Tracker {
    private config: PostHogTrackerConfig;

    eventHistory: TrackingEvent<BaseTrackEvent>[] = [];

    constructor(
        config: PostHogTrackerConfig = {
            dataConsentHandler: DEFAULT_DATA_CONSENT_HANDLER,
        },
    ) {
        this.config = config;
    }

    static queue = new EventQueue();

    private async hasConsent(): Promise<boolean> {
        const shouldTrackWithConsentOnly = isFeatureEnabled(
            (features) => features.posthogTrackingWithDataConsent,
        );

        if (shouldTrackWithConsentOnly) {
            try {
                return this.config.dataConsentHandler();
            } catch (error) {
                logger.error(error);
                logger.debug('[PostHog] Consent check failed, considering no consent.');
                return false;
            }
        }

        return true;
    }

    private async hasJuniorClearance(): Promise<boolean> {
        if (!this.config.isJuniorHandler) {
            logger.warn(
                '[PosthogTracker]: Junior check handler is not configured, therefore considering profile as junior.',
            );

            return false;
        }

        try {
            const isJunior = await this.config.isJuniorHandler();

            if (!isJunior) {
                return true;
            }

            logger.debug('[PosthogTracker]: Junior profile detected.');

            return isFeatureEnabled((features) => features.junior.posthogTrackingEnabled);
        } catch (error) {
            logger.error(error);
            logger.warn(
                '[PosthogTracker]: Junior check failed, therefore considering profile as junior.',
            );

            return false;
        }
    }

    async track(event: TrackingEvent<BaseTrackEvent>): Promise<void> {
        if (PostHogTracker.queue.isStopped) {
            return;
        }
        if (shouldTrackEventHistory()) {
            this.eventHistory.push(event);
        }
        if (!isFeatureEnabled((b) => b.posthogTracking)) {
            return;
        }

        const { apiGatewayUrl, postHogClientId, postHogApiKey, postHogGatewayUrl, postHogUrl } =
            getEnv();
        const apiKey = postHogClientId ?? postHogApiKey;

        if (!(apiGatewayUrl || postHogUrl || postHogGatewayUrl) || !apiKey) {
            logger.debug('PostHog url or PostHog api key are missing from the environment config.');
            return;
        }

        if (!(await this.hasConsent())) {
            return;
        }

        if (!(await this.hasJuniorClearance())) {
            logger.debug('[PosthogTracker]: Tracking disabled due to junior profile.');
            return;
        }

        const postHogTrackingProperties = getPostHogCrossApplicationTrackingProperties();

        if (!postHogTrackingProperties) {
            return;
        }

        const {
            sessionId,
            appName,
            appVersion,
            country,
            language,
            platform,
            osName,
            osVersion,
            deviceType,
            licenseId,
            userId,
        } = postHogTrackingProperties;

        let distinctId = postHogTrackingProperties.distinctId.toUpperCase();

        const shouldUseExternalIdAsDistinctId = getFeatureConfig(
            (config) => config.tracking?.posthog?.shouldUseExternalIdAsDistinctId,
        );

        if (shouldUseExternalIdAsDistinctId) {
            const { identifier } = await queryExternalIdentifiersIfNeeded();
            distinctId = identifier;
        }
        const uaParser = new UaParser();

        const body: PostHogDto = {
            api_key: apiKey,
            event: event.name,
            distinct_id: distinctId,
            $timestamp: new Date(event.timestamp).toISOString(),
            properties: {
                $session_id: sessionId,
                application: appName,
                $app_version: appVersion,
                platform,
                $os: osName,
                $browser: uaParser.getBrowser().name,
                $browser_version: uaParser.getBrowser().version,
                category: event.category,
                eventType: event.payload?.eventType ?? undefined,
                $current_url: window.location.href,
                normalized: event.source ?? 'n/a',
                $device_type: deviceType,
                app_language: language,
                app_country: country,
                user_id: userId,
                $os_version: osVersion,
                $groups: licenseId ? { license_id: licenseId } : undefined,
                ...event.payload,
            },
        };

        if (skipTracking()) {
            logger.info('PostHog#track', { ...body, api_key: '****' });
            return;
        }

        const eventBuilder = (): Promise<void> => {
            if (postHogGatewayUrl) {
                return request(`${postHogGatewayUrl}/capture`, {
                    body,
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    priority: 'low',
                });
            }
            if (apiGatewayUrl) {
                return request(`${apiGatewayUrl}posthog-proxy/public/capture`, {
                    body,
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    priority: 'low',
                });
            }
            // access posthog directly without going through gateway
            return request(`${postHogUrl}/capture`, {
                body,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                priority: 'low',
                skipAuthHeader: true,
            });
        };

        await PostHogTracker.queue.track(eventBuilder);
    }

    flushQueue(): Promise<void> {
        return PostHogTracker.queue.flushQueue();
    }

    reset(): void {
        return PostHogTracker.queue.reset();
    }

    stop(): void {
        return PostHogTracker.queue.stop();
    }

    updateConfig(config: Partial<PostHogTrackerConfig>): void {
        this.config = {
            ...this.config,
            ...config,
        };
    }

    resetHistory(): void {
        this.eventHistory = [];
    }
}
